import React, { useCallback } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import { Text } from '@atlaskit/primitives/compiled';

import type { UpdateWorkItemAllFieldTypesAction } from '../../../../../common/types';
import { FieldTooltipContent } from '../../../../../common/ui/FieldTooltipContent';
import {
	type RegisterTooltipProvider,
	useRegisterTooltipContentProvider,
} from '../../../../../common/utils/useRegisterTooltipContentProvider';
import { getActionData } from '../../../utils';

const messages = defineMessages({
	action: {
		id: 'update-work-item-all-fields-action-renderer.ui.action-card.flag-field.comment.non-final',
		defaultMessage: 'Add comment',
		description: 'Label for the change action in the comment field',
	},
});

export type Props = {
	action: UpdateWorkItemAllFieldTypesAction;
	registerTooltipProvider?: RegisterTooltipProvider;
};

export const CommentField = ({
	action,
	registerTooltipProvider,
}: Props): React.JSX.Element | null => {
	const { formatMessage } = useIntl();

	const { fieldKey, fieldValue, fieldReasoning } = getActionData(action);

	const isFieldRenderable = fieldKey === 'comment';

	const toolTipContentProvider = useCallback(
		() => (
			<FieldTooltipContent
				reasoning={fieldReasoning}
				suggested={fieldValue !== undefined && fieldValue !== null ? String(fieldValue) : undefined}
			/>
		),
		[fieldValue, fieldReasoning],
	);

	useRegisterTooltipContentProvider({
		registerTooltipProvider,
		fieldId: fieldKey,
		provider: toolTipContentProvider,
		enabled: isFieldRenderable,
	});

	const comment = String(fieldValue)?.trim();

	if (!isFieldRenderable || !comment) {
		return null;
	}

	return (
		<Text
			color="color.text.subtle"
			maxLines={1}
			testId="update-work-item-all-fields-action-renderer.ui.action-card.card-fields-list.comment-field"
		>
			{formatMessage(messages.action)}
		</Text>
	);
};
